import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setToken } from "@/lib/api";
import { OfflineGateway } from "@/lib/offline/gateway";
import { clearAllDeliveryDrafts, pruneDeliveryDrafts } from "@/lib/deliveryDraft";
import { registerTemperatureResolver } from "@/lib/offline/temperatureResolver";

// Register statutory conflict resolvers once (generic Needs-Attention architecture).
registerTemperatureResolver();

// Best-effort: stamp the offline principal + 12h auth window on every successful server
// validation. NEVER blocks auth; a native storage failure surfaces via the NOT-SAVED toast.
async function syncPrincipal(u) {
  try {
    if (u && u.user_id) {
      await OfflineGateway.setPrincipal({
        user_id: u.user_id, org_id: u.org_id, role: u.role,
        site_ids: u.site_ids || u.sites || [],
      });
    }
  } catch (e) { /* offline principal is best-effort */ }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=user
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState(null); // null=unknown/loading, {} = map

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncPrincipal(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.session_token);
    setUser(data.user);
    syncPrincipal(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.session_token);
    setUser(data.user);
    syncPrincipal(data.user);
    return data.user;
  };

  const personalSignup = async (payload) => {
    const { data } = await api.post("/auth/personal-signup", payload);
    setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const exchangeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    setToken(data.session_token);
    setUser(data.user);
    syncPrincipal(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { await OfflineGateway.detachOnLogout(); } catch (e) { /* never blocks logout */ }
    clearAllDeliveryDrafts();
    setToken(null);
    setUser(false);
  };

  const has = (perm) => user && user.permissions_effective?.includes(perm);

  useEffect(() => {
    if (user && user.user_id) pruneDeliveryDrafts(user); // another account's local drafts never survive a sign-in
  }, [user]);

  // Central module enablement (single fetch, drives both nav and route guarding).
  useEffect(() => {
    if (user && user.role !== "superadmin" && user.org_id) {
      api.get("/modules").then((r) => setModules(r.data.modules || {})).catch(() => setModules({}));
    } else {
      setModules(null);
    }
  }, [user]);
  // Default-enabled while unknown/loading — mirrors the backend "absence of config = default".
  const moduleEnabled = (key) => !key || !modules || modules[key] !== false;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, personalSignup, logout, exchangeGoogle, checkAuth, has, setUser, modules, moduleEnabled }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
